import LegalLayout from './LegalLayout.jsx';

/**
 * Privacy policy — concise version sized for a private-beta SaaS.
 * Covers the categories Meta App Review and GDPR both inspect:
 *   - what we collect (account data + connected-platform data + content)
 *   - lawful basis (contract performance + legitimate interest)
 *   - retention
 *   - sub-processors (third parties that touch personal data)
 *   - user rights + how to exercise them
 *   - contact for the data controller
 *
 * Per the global rule: privacy / terms pages are GDPR-exempt from the
 * "no competing-product / no LLM-brand names" rule, so the AI sub-processor
 * disclosure (Google Gemini) is fine here.
 */
export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="June 1, 2026">
      <section>
        <h2>1. Who we are</h2>
        <p>
          Markaii (operated by DCLACorp) is a multi-brand AI marketing platform.
          The data controller for the purposes of GDPR is DCLACorp, contactable at{' '}
          <a href="mailto:privacy@markaii.com">privacy@markaii.com</a>.
        </p>
      </section>

      <section>
        <h2>2. What we collect</h2>
        <h3>2.1 Account data</h3>
        <ul>
          <li>Email address, display name, hashed password</li>
          <li>Organization name + your role within it</li>
          <li>Login timestamps and IP address (security audit log)</li>
        </ul>
        <h3>2.2 Connected-platform data</h3>
        <p>When you connect a social account (Instagram, LinkedIn, etc.) we receive:</p>
        <ul>
          <li>The platform's user / business account ID and display handle</li>
          <li>An OAuth access token (encrypted at rest with AES-256)</li>
          <li>Page / IG-business linkage metadata required to publish</li>
          <li>Public engagement metrics (impressions, reach, likes, saves, shares, comments) on posts we published</li>
        </ul>
        <p>
          We never receive your social platform password. Tokens are scoped to the
          specific permissions you authorize during the OAuth consent screen and can
          be revoked any time from the Markaii dashboard or directly on the platform.
        </p>
        <h3>2.3 Content data</h3>
        <ul>
          <li>Brand profiles you create (positioning, voice, audience, features)</li>
          <li>AI-generated drafts and the prompts used to generate them</li>
          <li>Approval / rejection decisions and timestamps</li>
        </ul>
      </section>

      <section>
        <h2>3. Why we collect it (lawful basis)</h2>
        <ul>
          <li>
            <b>Contract performance</b> (Art. 6(1)(b) GDPR) — generating content, publishing
            on your behalf, reading engagement stats are the core service you signed up for.
          </li>
          <li>
            <b>Legitimate interest</b> (Art. 6(1)(f) GDPR) — security audit logs, abuse
            prevention, fraud detection.
          </li>
          <li>
            <b>Consent</b> (Art. 6(1)(a) GDPR) — only when explicit, e.g. connecting a
            new social platform via OAuth.
          </li>
        </ul>
      </section>

      <section>
        <h2>4. Sub-processors</h2>
        <p>We send personal data only to the following processors, each under a DPA:</p>
        <ul>
          <li><b>Google (Gemini API)</b> — generating marketing plans and content drafts. Your brand profile + prompts are sent; outputs are returned. Google does not train on this data per their API terms.</li>
          <li><b>Resend</b> — transactional email delivery (approval links, notifications). Sees email addresses only.</li>
          <li><b>Stripe</b> — payments and subscription management. Sees billing-related identifiers, never connected-platform tokens.</li>
          <li><b>Backblaze B2</b> — encrypted asset storage for generated images.</li>
          <li><b>Meta (Facebook / Instagram), LinkedIn, X, TikTok</b> — only the data and tokens you explicitly authorize during their respective OAuth flows.</li>
        </ul>
      </section>

      <section>
        <h2>5. Retention</h2>
        <ul>
          <li>Account data: retained until you close the account, then 30 days for billing reconciliation, then permanently deleted.</li>
          <li>Connected-platform tokens: retained until disconnected, then immediately purged from active storage (encrypted backups age out within 30 days).</li>
          <li>Generated content + audit logs: 24 months from creation (so historical engagement analysis works), then archived or deleted at your request.</li>
        </ul>
      </section>

      <section>
        <h2>6. Your rights (GDPR + UK GDPR)</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access — request a copy of all personal data we hold about you</li>
          <li>Rectification — correct inaccurate data</li>
          <li>Erasure — request deletion (see also <a href="/data-deletion">Data Deletion</a>)</li>
          <li>Restriction of processing</li>
          <li>Data portability — receive your data in a machine-readable format</li>
          <li>Object to processing based on legitimate interest</li>
          <li>Withdraw any consent at any time</li>
          <li>Lodge a complaint with your local data protection authority</li>
        </ul>
        <p>To exercise any of these rights, email <a href="mailto:privacy@markaii.com">privacy@markaii.com</a>. We respond within 30 days.</p>
      </section>

      <section>
        <h2>7. Security</h2>
        <p>
          All data in transit is TLS 1.3. Connected-platform OAuth tokens are encrypted
          at rest with AES-256 using a key stored separately from the database. Passwords
          are hashed with bcrypt. Access to production systems is restricted to specific
          DCLACorp personnel, gated by SSH key + 2FA.
        </p>
      </section>

      <section>
        <h2>8. International transfers</h2>
        <p>
          Servers are in the EU. Sub-processors listed in §4 may process data outside
          the EU under EU Standard Contractual Clauses or equivalent safeguards.
        </p>
      </section>

      <section>
        <h2>9. Cookies</h2>
        <p>
          The dashboard uses a single session cookie to keep you signed in. No analytics
          cookies, no advertising cookies, no third-party trackers on the landing page.
        </p>
      </section>

      <section>
        <h2>10. Changes to this policy</h2>
        <p>
          We post material changes at the top of this page and email account holders.
          The "Last updated" date above always reflects the current version.
        </p>
      </section>

      <section>
        <h2>11. Contact</h2>
        <p>
          Privacy questions or rights requests: <a href="mailto:privacy@markaii.com">privacy@markaii.com</a>.<br />
          General support: <a href="mailto:support@markaii.com">support@markaii.com</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
